import {
  Box,
  focusRing,
  Grid2 as Grid,
  IconButton,
  Menu,
  MenuItem,
  PlatformDialog,
} from "@platform/style";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/sharp-solid-svg-icons";
import * as React from "react";
import { Dispatch, SetStateAction } from "react";
import { WorkItem } from "../models/WorkItem";
import {
  collectGAEvent,
  fetchWithToken,
  getContext,
  getUserDetails,
  hasPermission,
  isFeatureEnabled,
  setContext,
  unsetContext,
  WorkItemContext,
} from "@platform/core";
import { reportError } from "../utils/errors";
import { getWorkItem, setWorkItem } from "../utils/storage";
import { WORK_ITEMS_BFF_API_BASE } from "../platform-workitems";
import { PopUpDialogBox } from "./PopUpDialogBox";
import { AssignWorkToWorkerDialog } from "./workItems/AssignWorkToWorkerDialog";
import { add, formatISO } from "date-fns";
import { ActionEnum } from "../models/ReasonsAndResolutions";
import createContext from "../utils/createContext";
import { ViewInactiveWIDetails } from "./workItems/ViewInactiveWIDetails";
import { addChatResponse } from "@platform/chatwindow-utils";
import { CancelWorkItemDialog } from "./CancelWorkItemDialog";
import { getIdentifier } from "../utils/getIdentifier";

interface WorkItemRowMenuProps {
  workItemId: string;
  dueDate: Date;
  followUpDate: string;
  onError: Dispatch<SetStateAction<string | null>>;
  refetch: Function;
  showMassActionIcons?: boolean;
  useAssignToMe?: boolean;
  useAssignToWorker?: boolean;
  assignedTo?: string;
  status?: string;
  setSnackBarState?: Dispatch<SetStateAction<boolean>>;
  setSnackBarMessage?: Dispatch<SetStateAction<string>>;
  isFeatureFlagged?: boolean;
  inactiveWIDetails?: WorkItem;
  workItemIsAssigned?: boolean;
}

export const WorkItemRowMenu = ({
  workItemId,
  dueDate,
  followUpDate,
  onError,
  refetch,
  showMassActionIcons = false,
  useAssignToMe,
  useAssignToWorker,
  assignedTo,
  status,
  setSnackBarState,
  setSnackBarMessage,
  isFeatureFlagged,
  inactiveWIDetails,
  workItemIsAssigned,
}: WorkItemRowMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [followUpAddOrChange, setFollowUpAddOrChange] = React.useState<string>("Add");
  const [popUpOpen, setPopUpOpen] = React.useState<boolean>(false);
  const [viewInactiveWIDetails, setViewInactiveWIDetails] = React.useState<boolean>(false);
  const [action, setAction] = React.useState<string | undefined>(undefined);
  const open = Boolean(anchorEl);
  const [assignWorkerOpen, setAssignWorkerOpen] = React.useState<boolean>(false);
  const [isManageWorkGroupsEnabled, setIsManageWorkGroupsEnabled] = React.useState<boolean>(false);
  const [allowAssign, setAllowAssign] = React.useState<boolean>(false);
  const [notAlreadyAssigned, setNotAlreadyAssigned] = React.useState<boolean>(false);
  const uid = getUserDetails().uid;

  React.useEffect(() => {
    if (useAssignToMe && (!assignedTo || assignedTo === "")) {
      setNotAlreadyAssigned(true);
    } else {
      setNotAlreadyAssigned(false);
    }
    (async () => {
      const allowAssign = await hasPermission("WorkManagementAdmin.assignWorkItem");
      setAllowAssign(allowAssign);
    })();
  }, [useAssignToMe, assignedTo]);

  React.useEffect(() => {
    if (isFeatureFlagged) {
      setIsManageWorkGroupsEnabled(true);
    } else {
      (async () => {
        const featureAssignToWorkers = await isFeatureEnabled("Feature.ManageWorkGroups");
        setIsManageWorkGroupsEnabled(featureAssignToWorkers);
      })();
    }
  }, []);

  const handleMenuButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    followUpDate ? setFollowUpAddOrChange("Change") : setFollowUpAddOrChange("Add");
    setAnchorEl(event.currentTarget);
  };

  const getTitle = () => {
    if (action == ActionEnum.ONHOLD) return `${followUpAddOrChange} Follow Up Date`;
    if (action == ActionEnum.CANCEL) return `Cancel Work Item`;
    if (action == ActionEnum.COMPLETE) return `Complete Work Item`;
    return "";
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewClick = async () => {
    let workItemFetchUri;
    let fetchMethod: string;

    collectGAEvent("menuItem", { key: "WorkItemView" });
    if (
      assignedTo === uid &&
      (status?.toUpperCase() === "NEW" || status?.toUpperCase() === "ON_HOLD")
    ) {
      workItemFetchUri = `${WORK_ITEMS_BFF_API_BASE}/${workItemId}/activate`;
      fetchMethod = "POST";
    } else {
      workItemFetchUri = `${WORK_ITEMS_BFF_API_BASE}/${workItemId}`;
      fetchMethod = "GET";
    }

    await fetchWithToken(workItemFetchUri, { method: fetchMethod })
      .then((response) => {
        if (!response.ok) {
          throw new Error(JSON.stringify(response));
        }

        return response.json();
      })
      .then((result) => {
        handleUpdatesForWorkItem(result, true);
        window.dispatchEvent(new Event("closeMyWorkItems"));
        window.dispatchEvent(new Event("closeWorkItems"));
      })
      .catch((error) => {
        onError(`Unable to open work item #${getIdentifier(workItemId)}. Please try again.`);
        reportError(error.message);
      });

    handleMenuClose();
  };

  ///////////////////
  //Cancel Section
  ///////////////////

  const [cancelDialogOpen, setCancelDialogOpen] = React.useState<boolean>(false);

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
    handleMenuClose();

    collectGAEvent("menuItem", { key: "CancelWorkItem" });
  };

  const handleCancelClose = () => {
    setCancelDialogOpen(false);
  };

  async function handleCancelSubmit(resolutionCode: string, comment: string) {
    await fetchWithToken(`${WORK_ITEMS_BFF_API_BASE}/${workItemId}/cancel`, {
      method: "POST",
      body: JSON.stringify({
        ResolutionCode: `${resolutionCode}`,
        Comment: comment ? `${comment}` : undefined,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(JSON.stringify(response));
        }
        if (getWorkItem()?.WorkItemIdentifier === workItemId) {
          unsetContext("workItem");
        }
        refetch();

        if (setSnackBarMessage) {
          setSnackBarMessage(`Work Item #${getIdentifier(workItemId)} has been cancelled.`);
        }
        if (setSnackBarState) {
          setSnackBarState(true);
        }
      })
      .catch((error) => {
        onError(`Unable to cancel work item #${getIdentifier(workItemId)}. Please try again.`);
        reportError(error.message);
      });
    setCancelDialogOpen(false);
  }

  ///////////////////
  // Complete WorkItem Section
  ///////////////////
  const handleCompleteWorkItem = async () => {
    setAction(ActionEnum.COMPLETE);
    setPopUpOpen(true);
    handleMenuClose();
    collectGAEvent("menuItem", { key: "WorkItemComplete" });
  };

  const handleCompleteSubmit = async (resolutionReasonCode: string, comment: string) => {
    await fetchWithToken(`${WORK_ITEMS_BFF_API_BASE}/${workItemId}/complete`, {
      method: "POST",
      body: JSON.stringify({
        ResolutionCode: `${resolutionReasonCode}`,
        Comment: comment ? `${comment}` : undefined,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(JSON.stringify(response));
        }
        refetch();

        if (setSnackBarMessage) {
          setSnackBarMessage(`Work Item #${getIdentifier(workItemId)} has been completed.`);
        }
        if (setSnackBarState) {
          setSnackBarState(true);
        }
      })
      .catch((error) => {
        onError(`Unable to complete work item #${getIdentifier(workItemId)}. Please try again.`);
        reportError(error.message);
      });
    setPopUpOpen(false);
  };

  ///////////////////
  //Assign to Me Section
  ///////////////////
  const handleAssignToMeClick = async () => {
    collectGAEvent("menuItem", { key: "WorkItemAssignToMe" });
    await fetchWithToken(`${WORK_ITEMS_BFF_API_BASE}/${workItemId}/claim`, {
      method: "POST",
      body: JSON.stringify({ comments: "Claiming work item" }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(JSON.stringify(response));
        }
        return response.json();
      })
      .then((result) => {
        refetch();
        if (setSnackBarMessage) {
          setSnackBarMessage("Work Item has been assigned to you");
        }
        if (setSnackBarState) {
          setSnackBarState(true);
        }

        handleUpdatesForWorkItem(result, false);
      })
      .catch((error) => {
        onError(`Unable to assign work item #${getIdentifier(workItemId)}. Please try again.`);
        reportError(error.message);
      });

    handleMenuClose();
  };

  ///////////////////
  //Assign to Worker Section
  ///////////////////

  const handleAssignToWorkerClick = async () => {
    setAssignWorkerOpen(true);
    handleMenuClose();
    collectGAEvent("menuItem", { key: "WorkItemAssignToWorker" });
  };

  const handleAssignToWorkerClose = () => {
    setAssignWorkerOpen(false);
  };

  ////////////////////////
  //Handle Pop Up Section
  ///////////////////////

  async function handlePopUpSubmit(
    resolutionReasonCode: string,
    comment: string,
    selectedDate?: Date | null
  ) {
    if (action == ActionEnum.ONHOLD) {
      await handleFollowUpSubmit(resolutionReasonCode, comment, selectedDate!);
    }
    if (action == ActionEnum.CANCEL) {
      await handleCancelSubmit(resolutionReasonCode, comment);
    }
    if (action == ActionEnum.COMPLETE) {
      await handleCompleteSubmit(resolutionReasonCode, comment);
    }
    setAction(undefined);
    setPopUpOpen(false);
  }

  const handlePopUpModalClose = async () => {
    setAction(undefined);
    setPopUpOpen(false);
  };

  ///////////////////
  //Follow Up Section
  ///////////////////
  async function handleFollowUpSubmit(
    reasonCode: string,
    comment: string,
    selectedDate: Date | null
  ) {
    const requestBody = {
      FollowUpDate: selectedDate ? formatISO(selectedDate, { representation: "date" }) : undefined,
      ReasonCode: `${reasonCode}`,
      Comment: comment ? `${comment}` : undefined,
    };

    await fetchWithToken(`${WORK_ITEMS_BFF_API_BASE}/${workItemId}/hold`, {
      method: "POST",
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(JSON.stringify(response));
        }
        return response.json();
      })
      .then((result) => {
        refetch();

        if (setSnackBarMessage) {
          setSnackBarMessage(
            `Follow-up date for Work Item #${getIdentifier(workItemId)} has been set.`
          );
        }
        if (setSnackBarState) {
          setSnackBarState(true);
        }

        handleUpdatesForWorkItem(result, false);
      })
      .catch((error) => {
        onError(
          `Unable to ${followUpAddOrChange.toLowerCase()} follow up date on work item #${getIdentifier(
            workItemId
          )}. Please try again.`
        );
        reportError(error.message);
      });
  }

  ///////////////////
  //Common Section
  ///////////////////
  //do this if the work item in context is same as the work item being changed
  const handleUpdatesForWorkItem = (changedWorkItem: WorkItem, setInContext: boolean) => {
    const currentWorkItemInContext = getContext("workItem") as WorkItemContext;
    const currentWorkItemInStorage = getWorkItem();
    if (currentWorkItemInContext && currentWorkItemInContext.WorkItemId === workItemId) {
      setWorkItem(changedWorkItem);
      didStatusInReceiptChange(
        changedWorkItem,
        currentWorkItemInStorage,
        currentWorkItemInContext
      ) && addChatResponse("platform-workitems-chatContent-workItemBubble");
      window.dispatchEvent(new Event("updateWorkItem"));
    } else if (setInContext) {
      setWorkItem(changedWorkItem);
      setContext("workItem", createContext(changedWorkItem));
    }
  };

  function didStatusInReceiptChange(
    newWorkItem: WorkItem,
    currentWorkItemInStorage: WorkItem | undefined,
    currentWorkItemInContext: WorkItemContext | undefined
  ) {
    return (
      currentWorkItemInStorage?.Status !== newWorkItem.Status &&
      currentWorkItemInStorage?.WorkItemIdentifier == currentWorkItemInContext?.WorkItemId
    );
  }

  function getLoanNumber(): string {
    if (inactiveWIDetails && "Associations" in inactiveWIDetails) {
      const associationObject = inactiveWIDetails.Associations[0];
      //TODO: Do we need to check if context of wi is "Loan" ? What if context is not "Loan" ?
      try {
        const associatedContext = JSON.parse(associationObject.Key);
        return associatedContext.loanIdentifier.toString();
      } catch {
        return "";
      }
    }
    return "";
  }

  return (
    <>
      {!showMassActionIcons && (
        <IconButton
          aria-label={`work item ${workItemId} menu`}
          id={`work-item-${workItemId}-button`}
          aria-controls={open ? `work-item-${workItemId}-menu` : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleMenuButtonClick}
          size="small"
          sx={{ boxSizing: "content-box", padding: 2 }}
        >
          <Box sx={{ color: "primary.main" }}>
            <FontAwesomeIcon icon={faEllipsisV} aria-label={"WorkItemMoreIcon"} />
          </Box>
        </IconButton>
      )}
      <Menu
        slotProps={{
          paper: { sx: { width: "auto", borderRadius: "5px", marginTop: "-32px !important" } },
        }}
        id={`work-item-${workItemId}-menu`}
        aria-labelledby={`work-item-${workItemId}-button`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem
          onClick={
            inactiveWIDetails
              ? () => {
                  setViewInactiveWIDetails(true);
                  handleMenuClose();
                }
              : handleViewClick
          }
          sx={focusRing}
        >
          View Work Item
        </MenuItem>
        {!inactiveWIDetails && (
          <MenuItem onClick={handleCancelClick} sx={focusRing}>
            Cancel Work Item
          </MenuItem>
        )}
        {notAlreadyAssigned && !inactiveWIDetails && (
          <MenuItem onClick={handleAssignToMeClick} sx={focusRing}>
            Assign to Me
          </MenuItem>
        )}
        {allowAssign && !inactiveWIDetails && isManageWorkGroupsEnabled && (
          <MenuItem onClick={handleAssignToWorkerClick} sx={focusRing}>
            Assign to Worker
          </MenuItem>
        )}

        {!inactiveWIDetails && status?.toUpperCase() !== "NEW" && (
          <MenuItem
            onClick={() => {
              setAction(ActionEnum.ONHOLD);
              setPopUpOpen(true);
              collectGAEvent("menuItem", { key: "WorkItemFollowUpDate" });
              handleMenuClose();
            }}
            sx={focusRing}
          >
            {followUpAddOrChange} Follow-up Date
          </MenuItem>
        )}

        {!inactiveWIDetails && (
          <MenuItem onClick={handleCompleteWorkItem} sx={focusRing}>
            Complete Work Item
          </MenuItem>
        )}
      </Menu>

      {action && (
        <PopUpDialogBox
          optional={action === ActionEnum.ONHOLD}
          typeOfAction={action}
          maxDate={dueDate ?? add(new Date(), { days: 10 })}
          onSubmit={handlePopUpSubmit}
          open={popUpOpen}
          onClose={handlePopUpModalClose}
          title={getTitle()}
        />
      )}

      <CancelWorkItemDialog
        workItemId={workItemId}
        open={cancelDialogOpen}
        onClose={handleCancelClose}
        onSubmit={() => {
          setCancelDialogOpen(false);
          setAction(ActionEnum.CANCEL);
          setPopUpOpen(true);
        }}
      />

      <AssignWorkToWorkerDialog
        open={assignWorkerOpen}
        onClose={handleAssignToWorkerClose}
        handleUpdatesForWorkItem={handleUpdatesForWorkItem}
        onError={onError}
        refetch={refetch}
        workItemId={workItemId}
        setAssignWorkerOpen={setAssignWorkerOpen}
        setSnackBarMessage={setSnackBarMessage}
        setSnackBarState={setSnackBarState}
        workItemAssignedTo={workItemIsAssigned}
      />

      {inactiveWIDetails && (
        <PlatformDialog
          open={viewInactiveWIDetails}
          title={"Inactive Work Item"}
          onClose={() => {
            setViewInactiveWIDetails(false);
          }}
          maxWidth={500}
        >
          <Grid container direction="column" justifyContent="space-around" alignItems="center">
            <Grid sx={{ width: "440px" }}>
              <ViewInactiveWIDetails workItem={inactiveWIDetails} loanNumber={getLoanNumber()} />
            </Grid>
          </Grid>
        </PlatformDialog>
      )}
    </>
  );
};
